import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_NAME = "Strato Lab 3D";
const CANONICAL_ORIGIN = "https://stratolab3d.com";
const INDEXABLE_ROBOTS = "index,follow";
const DEFAULT_DESCRIPTION =
  "Strato Lab 3D trasforma file 3D, idee e componenti in pezzi stampati in FDM con valutazione tecnica personalizzata.";

const ROUTE_META = {
  "/": {
    title: "Strato Lab 3D — Dal file al pezzo reale",
    description: DEFAULT_DESCRIPTION,
  },
  "/servizi": {
    title: "Servizi di stampa 3D FDM — Strato Lab 3D",
    description: "Prototipi, componenti tecnici, ricambi e oggetti personalizzati stampati in 3D su richiesta.",
  },
  "/materiali": {
    title: "Materiali e colori FDM — Strato Lab 3D",
    description: "Materiale PLA, colori disponibili e finiture valutate nel preventivo tecnico.",
  },
  "/come-funziona": {
    title: "Come funziona — Strato Lab 3D",
    description: "Dal file o dall'idea al preventivo tecnico, fino alla produzione dopo conferma.",
  },
  "/metodo": {
    title: "Metodo Strato — Strato Lab 3D",
    description: "Il metodo Strato per analisi tecnica, produzione controllata e gestione riservata dei file.",
  },
  "/stato-richiesta": {
    title: "Stato richiesta — Strato Lab 3D",
    description: "Controlla lo stato del tuo progetto con codice richiesta ed email associata.",
  },
  "/chi-siamo": {
    title: "Chi siamo — Strato Lab 3D",
    description: "Strato Lab 3D è un laboratorio di stampa 3D FDM orientato a richieste tecniche su misura.",
  },
  "/contatti": {
    title: "Contatti — Strato Lab 3D",
    description: "Contatta Strato Lab 3D o avvia un preventivo tecnico per il tuo progetto di stampa 3D.",
  },
  "/faq": {
    title: "FAQ — Strato Lab 3D",
    description: "Risposte su file, materiali, preventivo tecnico, tempi, riservatezza e pagamento.",
  },
  "/preventivo": {
    title: "Avvia il preventivo — Strato Lab 3D",
    description: "Richiedi una valutazione tecnica guidata per un file 3D, un'idea o un componente da ristampare.",
  },
  "/privacy-policy": {
    title: "Privacy Policy — Strato Lab 3D",
    description: "Informazioni sul trattamento dei dati personali per richieste, file tecnici e gestione del servizio.",
  },
  "/cookie-policy": {
    title: "Cookie Policy — Strato Lab 3D",
    description: "Informazioni sull'uso di cookie tecnici e servizi collegati al sito Strato Lab 3D.",
  },
  "/termini-condizioni": {
    title: "Termini e condizioni — Strato Lab 3D",
    description: "Condizioni di utilizzo e regole del servizio di stampa 3D su richiesta.",
  },
  "/resi-rimborsi": {
    title: "Resi e rimborsi — Strato Lab 3D",
    description: "Indicazioni su resi, rimborsi e pezzi personalizzati realizzati su richiesta.",
  },
  "/spedizioni": {
    title: "Spedizioni — Strato Lab 3D",
    description: "Informazioni su consegna, ritiro e spedizione dei pezzi prodotti da Strato Lab 3D.",
  },
  "/informativa-legale": {
    title: "Informativa legale — Strato Lab 3D",
    description: "Informazioni legali e note di responsabilità del servizio Strato Lab 3D.",
  },
};

const LEGACY_CANONICALS = {
  "/privacy": "/privacy-policy",
  "/cookie": "/cookie-policy",
  "/termini": "/termini-condizioni",
  "/resi": "/resi-rimborsi",
};

export function Seo() {
  const location = useLocation();

  useEffect(() => {
    const pathname = normalizePath(location.pathname);
    const canonicalPath = LEGACY_CANONICALS[pathname] || pathname;
    const meta = routeMeta(pathname);
    const isNotFound = !meta;
    const title = meta?.title || `Pagina non trovata — ${SITE_NAME}`;
    const description = meta?.description || DEFAULT_DESCRIPTION;

    document.documentElement.lang = "it";
    document.title = title;
    setMeta("description", description);
    if (isNotFound) {
      removeMeta("robots");
    } else {
      setMeta("robots", INDEXABLE_ROBOTS);
    }
    setLink("canonical", `${CANONICAL_ORIGIN}${canonicalPath}`);
    setProperty("og:site_name", SITE_NAME);
    setProperty("og:type", "website");
    setProperty("og:title", title);
    setProperty("og:description", description);
    setProperty("og:url", `${CANONICAL_ORIGIN}${canonicalPath}`);
    setMeta("twitter:card", "summary");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
  }, [location.pathname]);

  return null;
}

function normalizePath(pathname) {
  if (!pathname || pathname === "/") return "/";
  return pathname.replace(/\/+$/, "") || "/";
}

function routeMeta(pathname) {
  if (ROUTE_META[pathname]) return ROUTE_META[pathname];
  if (pathname.startsWith("/servizi/")) {
    return {
      title: "Servizi stampa 3D — Strato Lab 3D",
      description: "Applicazioni reali per prototipi, componenti tecnici e parti stampate in 3D.",
    };
  }
  return null;
}

function setMeta(name, content) {
  let element = document.head.querySelector(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("name", name);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function removeMeta(name) {
  const element = document.head.querySelector(`meta[name="${name}"]`);
  if (element) {
    element.remove();
  }
}

function setProperty(property, content) {
  let element = document.head.querySelector(`meta[property="${property}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("property", property);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function setLink(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}
